import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { MessageSquare, Send, MapPin, Calendar, Lock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function CommunityPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    location: '',
    date_occurred: ''
  });

  useEffect(() => {
    const fetchStories = async () => {
      try {
        const res = await axios.get(`${API}/community/stories?status=approved`);
        setStories(res.data);
      } catch (error) {
        console.error('Error fetching community stories:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStories();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.content) {
      toast.error('Please fill in title and content');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/community/stories`, formData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      
      toast.success('Story submitted for review!');
      setShowForm(false);
      setFormData({ title: '', content: '', location: '', date_occurred: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error submitting story');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-12 px-4 md:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <MessageSquare className="w-8 h-8 text-cyan-400" />
            <h1 className="font-heading text-3xl md:text-5xl font-bold" data-testid="community-title">
              Community Stories
            </h1>
          </div>
          <p className="text-white/50 max-w-2xl">
            Share your personal experiences and encounters. All submissions are reviewed before publishing.
          </p>
        </div>

        {/* Submit Button */}
        <div className="mb-8">
          {user ? (
            <Button 
              onClick={() => setShowForm(!showForm)} 
              className={showForm ? 'btn-outline' : 'btn-primary'}
              data-testid="toggle-form-btn"
            >
              {showForm ? 'Cancel' : 'Share Your Story'}
            </Button>
          ) : (
            <div className="flex items-center gap-4">
              <Lock className="w-5 h-5 text-white/30" />
              <span className="text-white/50">Sign in to share your story</span>
              <Button onClick={() => navigate('/auth')} className="btn-outline" data-testid="signin-btn">
                Sign In
              </Button>
            </div>
          )}
        </div>

        {/* Submission Form */}
        {showForm && user && (
          <form onSubmit={handleSubmit} className="card-dark p-6 mb-12 animate-fade-in-up" data-testid="story-form">
            <h3 className="font-heading text-xl font-semibold mb-6">Submit Your Experience</h3>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-mono uppercase tracking-wider text-white/40 mb-2">
                  Title *
                </label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Brief title for your experience"
                  className="bg-white/5 border-white/10"
                  data-testid="story-title-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-mono uppercase tracking-wider text-white/40 mb-2">
                  Your Experience *
                </label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Describe your experience in detail..."
                  rows={8}
                  className="bg-white/5 border-white/10"
                  data-testid="story-content-input"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-mono uppercase tracking-wider text-white/40 mb-2">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Location (optional)
                  </label>
                  <Input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="City, State/Country"
                    className="bg-white/5 border-white/10"
                    data-testid="story-location-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-mono uppercase tracking-wider text-white/40 mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Date Occurred (optional)
                  </label>
                  <Input
                    type="text"
                    value={formData.date_occurred}
                    onChange={(e) => setFormData({ ...formData, date_occurred: e.target.value })}
                    placeholder="e.g., Summer 2019"
                    className="bg-white/5 border-white/10"
                    data-testid="story-date-input"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-4 pt-4">
                <Button type="submit" className="btn-primary flex items-center gap-2" disabled={submitting} data-testid="submit-story-btn">
                  <Send className="w-4 h-4" />
                  {submitting ? 'Submitting...' : 'Submit for Review'}
                </Button>
                <span className="text-white/30 text-sm">All submissions are reviewed before publishing</span>
              </div>
            </div>
          </form>
        )}

        {/* Stories List */}
        <div className="space-y-6" data-testid="stories-list">
          {loading ? (
            [...Array(3)].map((_, i) => (
              <div key={i} className="skeleton h-48 rounded-sm" />
            ))
          ) : stories.length === 0 ? (
            <div className="text-center py-20 card-dark">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 text-white/20" />
              <p className="text-white/50">No approved stories yet. Be the first to share!</p>
            </div>
          ) : (
            stories.map(story => (
              <div key={story.community_story_id} className="card-dark p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h3 className="font-heading text-xl font-semibold mb-1">{story.title}</h3>
                    <p className="text-cyan-400/70 text-sm font-mono">
                      Shared by {story.user_name}
                    </p>
                  </div>
                  <CheckCircle className="w-5 h-5 text-green-500/70 flex-shrink-0" title="Verified" />
                </div>
                
                <p className="text-white/70 leading-relaxed whitespace-pre-wrap mb-4">{story.content}</p>
                
                <div className="flex items-center gap-4 text-white/40 text-sm">
                  {story.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {story.location}
                    </span>
                  )}
                  {story.date_occurred && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {story.date_occurred}
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
